import { getHtmlLanguage, hasText, withQuery } from "./helpers"
import AdminInitialState from "models/admin-initial-state"
import {
  ClassroomGameStartedTeacherResponse,
  ClientStudent,
  ConnectAnswerRequest,
  DragDropAnswerRequest,
  DrawingSubmitRequest,
  JoinResponse,
  KeyboardAnswerRequest,
  MapAnswerRequest,
  NewClassroomGameOptions,
  NewClassroomGameRequest,
  NewClassroomGameResponse,
  NextScreenRequest,
  OpenTextAnswerRequest,
  PendingMessagesResponse,
  PollAnswerRequest,
  PreviewScreen,
  QuizAnswerRequest,
  ReconnectResponse,
  ReconnectSessionResponse,
  ReconnectTeacherResponse,
  ReviewScreenResponse,
  ScreenFinishedResponse,
  ToggleTimerRequest,
  ToggleTimerResponse,
} from "play/play-model"
import {
  ConnectPair,
  DragDropAnswer,
  DragDropCategory,
  ImageType,
  KnownOrderPackageTypes,
  Lesson,
  LessonScreen,
  LessonSearchInfo,
  LessonVisibility,
  LessonWithValidation,
  License,
  MapEllipseAnswer,
  PaymentType,
  PlaySession,
  PlaySessionCertificate,
  PlaySessionCertificateResponse,
  PlaySessionWithStats,
  PreviewScreenChangedRequest,
  PreviewScreenResponse,
  PreviewScreenResponseRaw,
  QuizAnswersLayouts,
  QuizScreen,
  Report,
  ReportIndexForWeb,
  ReportWithPlaySessionAndStats,
  ScreenType,
  Standard,
  UnsplashImage,
  ValidatedResource,
  Theme,
} from "models/tables"
import StudentClassResponse, {
  AiAudience,
  AudioUploadResult,
  ClassResponse,
  ClassSearchResponse,
  DashboardBlogPost,
  DashboardContents,
  DashboardLeaderboardSchool,
  DiscoverPageResult,
  ErrorResult,
  ImageResizeResult,
  ImageUploadResult,
  KhanAcademyVideo,
  LoggedInUser,
  NvoStatsForClass,
  Page,
  Pagination,
  SchoolAllClasses,
  SimplePage,
  Student,
  StudentReport,
  UserReportsPage,
} from "models/dto"
import {
  RawRequestResult,
  serverDelete,
  ServerMethod,
  serverPost,
  serverPut,
  serverRequest,
} from "./server-api"
import { serverGet } from "utils/server-api"

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Preview
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export function serverPreviewScreenChanged(
  requestData: PreviewScreenChangedRequest
): Promise<void> {
  return serverPost(`/api/admin/preview-screen`, requestData).then(() => {})
}

export function serverGetLatestPreviewScreen(
  lessonId: string
): Promise<PreviewScreenResponse> {
  return serverGet(
    `/api/admin/preview-screen/${encodeURIComponent(lessonId)}`
  ).then((resp) => {
    const raw = resp.body as PreviewScreenResponseRaw
    if (raw.latest) {
      return {
        ...raw,
        latest: {
          ...raw.latest,
          timestamp: new Date(raw.latest.timestamp),
          theme: raw.latest.theme || null,
        },
      }
    } else {
      return {
        ...raw,
        latest: undefined,
      }
    }
  })
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Profile
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export type ProfileChange = Partial<LoggedInUser>

export function serverUpdateProfile(
  update: ProfileChange
): Promise<ProfileChange> {
  return serverPut("/api/profile", update).then(
    (raw) => raw.body as ProfileChange
  )
}

export function serverUploadProfilePicture(
  file: File
): Promise<ImageResizeResult> {
  return serverPost("/api/images/profile-picture", file).then((raw) => {
    const resp = raw.body as ImageResizeResult[]
    return resp[0]
  })
}

export function serverUploadEnvles(file: File): Promise<{ lessonId: string }> {
  return serverPost("/api/import-envles", file).then(
    (raw) => raw.body as { lessonId: string }
  )
}

export function serverProfileGetClasses(): Promise<StudentClassResponse[]> {
  return serverGet("/api/profile/classes").then(
    (raw) => raw.body as StudentClassResponse[]
  )
}

export interface AddStudentToClassRequest {
  classId: string
  fullName: string | null
  email: string | null
}

export type AddStudentToClassResponse =
  | {
      student: undefined
      error: string
    }
  | {
      student: Student
      error: undefined
    }

export function serverAddOrCreateStudentToClass(
  request: AddStudentToClassRequest
): Promise<AddStudentToClassResponse> {
  return serverPost(`/api/classes/${request.classId}/students`, {
    fullName: request.fullName,
    email: request.email,
  }).then((raw) => raw.body as AddStudentToClassResponse)
}

export enum AddUserToClassError {
  None,
  UserNotFound,
  InvitationCodeInvalid,
  ClassNotFound,
  MaxNumberOfStudentsPerClassReached,
  NameOrEmailRequired,
  InvalidEmail,
}

export function serverProfileJoinClass(request: {
  invitationCode: string
}): Promise<{ error: AddUserToClassError }> {
  return serverPut("/api/profile/join-class", request).then(
    (raw) => raw.body as { error: AddUserToClassError }
  )
}

export function createCertificateLink(request: {
  title: string
  studentName: string
  date: Date
  teacherName: string
}) {
  return serverPost("/api/create-certificate-link", request).then(
    (raw) => raw.body as string
  )
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Users
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export function serverGetNvoStatsForClass(
  classId: string
): Promise<NvoStatsForClass> {
  return serverGet(`/api/users/nvo-stats-class/${classId}`).then(
    (raw) => raw.body as NvoStatsForClass
  )
}

export function serverCheckIfUserHasPaidNVO(): Promise<boolean> {
  return serverGet("/api/users/nvo-lic").then((raw) => raw.body as boolean)
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Map Answer
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export function serverCreateMapEllipseAnswer(
  mapId: string
): Promise<MapEllipseAnswer> {
  return serverPost("/api/map-ellipse-answer", { mapId }).then(
    (raw) => raw.body as MapEllipseAnswer
  )
}

export function serverDeleteMapEllipseAnswer(answer: {
  id: string
}): Promise<any> {
  return serverDelete(`/api/map-ellipse-answer/${answer.id}`, {
    parseResponse: false,
  })
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Drag-drop
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export function serverCreateDragDropCategory(
  category: DragDropCategory
): Promise<ValidatedResource<DragDropCategory>> {
  return serverPut("/api/drag-drop-category", category).then(
    (raw) => raw.body as ValidatedResource<DragDropCategory>
  )
}

export function serverDeleteDragDropCategory(id: string): Promise<void> {
  return serverDelete(`/api/drag-drop-category/${id}`, {
    parseResponse: false,
  }).then(() => {})
}

export function serverCreateDragDropAnswer(
  categoryId: string
): Promise<ValidatedResource<DragDropAnswer>> {
  return serverPut("/api/drag-drop-answer", { categoryId }).then(
    (raw) => raw.body as ValidatedResource<DragDropAnswer>
  )
}

export function serverDeleteDragDropAnswer(answerId: string): Promise<any> {
  return serverDelete(`/api/drag-drop-answer/${answerId}`, {
    parseResponse: false,
  })
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Screens
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export function screenServerUrl(screen: LessonScreen): string {
  const lang = getHtmlLanguage()
  let url = `/${lang}/api/`
  switch (screen.type) {
    case ScreenType.Presentation:
      url += "presentation-screen"
      break

    case ScreenType.Map:
      url += "map-screen"
      break

    case ScreenType.Quiz:
      url += "quiz-screen"
      break

    case ScreenType.Keyboard:
      url += "keyboard-screen"
      break

    case ScreenType.Drawing:
      url += "draw-screen"
      break

    case ScreenType.Connect:
      url += "connect-screen"
      break

    case ScreenType.DragDrop:
      url += "drag-drop-screen"
      break

    case ScreenType.Poll:
      url += "poll-screen"
      break

    case ScreenType.OpenText:
      url += "open-text-screen"
      break

    default:
      throw "Can't save screen: Unknown screen type"
  }

  if (screen.id) {
    url += "/" + screen.id
  }

  url += "/"

  return url
}

export function serverUpdateScreen<TScreen extends LessonScreen>(
  screen: TScreen
): Promise<ValidatedResource<TScreen>> {
  let url: string

  try {
    url = screenServerUrl(screen)
  } catch (error) {
    return new Promise((resolve, reject) => {
      let error: ErrorResult = {
        statusCode: 0,
        message: "Can't save screen: Unknown screen type",
        body: null,
      }
      console.error(error.message)
      reject(error)
    })
  }

  let verb: ServerMethod
  if (screen.id) {
    verb = "PUT"
  } else {
    verb = "POST"
  }

  return serverRequest(verb, url, screen).then(
    (raw) => raw.body as ValidatedResource<TScreen>
  )
}

export function serverDuplicateScreen(
  screen: LessonScreen
): Promise<LessonScreen> {
  return serverPut("/api/screens/duplicate", screen).then(
    (raw) => raw.body as LessonScreen
  )
}

export function serverAddScreenToLesson(
  lessonId: string,
  screen: LessonScreen
): Promise<ValidatedResource<LessonScreen>> {
  return serverPost(
    `/api/lessons/${encodeURIComponent(lessonId)}/screens`,
    screen
  ).then((raw) => raw.body as ValidatedResource<LessonScreen>)
}

export function serverDeleteScreen(id: string): Promise<any> {
  return serverDelete(`/api/screens/${id}`)
}

export type CreateMultipleScreensSlide = {
  imageUrl: string
  type: ScreenType
}

export type CreateMultipleScreensRequest = {
  lessonId: string
  slides: CreateMultipleScreensSlide[]
}

export function serverCreateMultipleScreens(
  req: CreateMultipleScreensRequest
): Promise<ValidatedResource<LessonScreen>[]> {
  return serverPut(`/api/screens/multiple-from-pictures`, req).then(
    (raw) => raw.body as ValidatedResource<LessonScreen>[]
  )
}

type CreateScreenRequest = {
  lessonId: string
  type: ScreenType
  selectedScreenId: string | null
  quizAnswerLayout?: QuizAnswersLayouts
}

export function serverCreateScreen(
  createScreenRequest: CreateScreenRequest
): Promise<ValidatedResource<LessonScreen>> {
  return serverPut("/api/screens", createScreenRequest).then(
    (raw) => raw.body as ValidatedResource<LessonScreen>
  )
}

export function serverGetScreenOrder(request: {
  lessonId: string
}): Promise<string[]> {
  return serverGet(`/api/screens/order?lessonId=${request.lessonId}`).then(
    (raw) => raw.body as string[]
  )
}

export type SearchImagesResult = {
  items: ImageUploadResult[]
  lastId?: string
  nextUrl?: string
}

type SearchImagesOptions = {
  filter: string
  type: ImageType
  lastId?: string | null
}

export function serverSearchLocalImages(
  opts: SearchImagesOptions
): Promise<SearchImagesResult> {
  const type = encodeURIComponent(ImageType[opts.type])

  let url = withQuery(`/api/images/local/${type}`, {
    term: opts.filter?.trim(),
    lastId: opts.lastId,
  })

  return serverGet(url).then((x) => x.body as SearchImagesResult)
}

type SearchUnsplashImagesOptions = {
  term: string
  page: number
  type: ImageType
}

export type SearchUnsplashResultItem = {
  upload: ImageUploadResult
  unsplashImage: UnsplashImageInfo
}

export type SearchUnsplashResult = {
  items: SearchUnsplashResultItem[]
  nextUrl?: string
}

export type UnsplashImageInfo = UnsplashImage & {
  webUrl: string
  desktopUrl: string
  width: number
  height: number
}

export function serverSearchUnsplashImages(
  opts: SearchUnsplashImagesOptions
): Promise<SearchUnsplashResult> {
  const typeName = ImageType[opts.type]

  return serverGet(
    withQuery(`/api/images/unsplash/search/${typeName}`, {
      page: opts.page,
      term: opts.term,
    })
  ).then((x) => x.body as SearchUnsplashResult)
}

export type TrackDownloadRequest = {
  lessonId: string
  image: UnsplashImageInfo
}

export function serverTrackUnsplashPhoto(
  request: TrackDownloadRequest
): Promise<void> {
  return serverPost(`/api/images/unsplash/track-download`, request).then(
    () => {}
  )
}

export interface SearchScreensResult {
  term: string
  page: number
  hasMore: boolean
  screens: LessonScreen[]
}

export function serverSearchScreens(
  filter: string,
  page: number,
  lastScreenId?: string
): Promise<SearchScreensResult> {
  const theFilter = encodeURIComponent(filter)
  return serverGet(
    withQuery(`/api/screens/search/${theFilter}`, {
      page,
      lastScreenId,
    })
  ).then((raw) => raw.body as SearchScreensResult)
}

export function serverGetScreenPreview(id: string): Promise<PreviewScreen> {
  return serverGet(`/api/screens/preview/${encodeURIComponent(id)}`).then(
    (x) => x.body as PreviewScreen
  )
}

export function serverGetBusinessPredefinedLessons(): Promise<Lesson[]> {
  return serverGet(`/api/lessons/discover/business`).then(
    (x) => x.body as Lesson[]
  )
}

export function serverGetSchoolDiscoverPage(): Promise<DiscoverPageResult> {
  return serverGet(`/api/lessons/discover/school`).then(
    (x) => x.body as DiscoverPageResult
  )
}

export function serverGetLatestLessons(
  latestId: string | null
): Promise<Pagination<LessonSearchInfo>> {
  return serverGet(`/api/lessons/latest/${latestId || ""}`).then((x) => {
    return x.body as Pagination<LessonSearchInfo>
  })
}

export function serverGetPromotedLessons(
  latestId: string | null
): Promise<Pagination<LessonSearchInfo>> {
  return serverGet(`/api/lessons/promoted/${latestId || ""}`).then((x) => {
    return x.body as Pagination<LessonSearchInfo>
  })
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Connect pairs
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export function serverCreateConnectPair(
  pair: ConnectPair
): Promise<ConnectPair> {
  return serverPost(`/api/connect-pair/`, pair).then(
    (raw) => raw.body as ConnectPair
  )
}

export function serverUpdateConnectPair(
  pair: ConnectPair
): Promise<ConnectPair> {
  return serverPut(`/api/connect-pair/${pair.id}`, pair).then(
    (raw) => raw.body as ConnectPair
  )
}

export function serverDeleteConnectPair(id: string): Promise<any> {
  return serverDelete(`/api/connect-pair/${id}`, { parseResponse: false })
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Lessons
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export interface LessonsFilter {
  visibility: LessonVisibility
  grades: string[]
  filter: string
  subjects: string[]
  page: number
}

function prepareLessonForServer(lesson: Lesson): any {
  let result: any
  result = lesson
  // NOTE: now this is an array
  // result.grades = packStrings(lesson.grades);
  // result.subjects = packStrings(lesson.subjects);
  return result
}

function fixLessonFromServerResponse(
  raw: RawRequestResult
): ValidatedResource<Lesson> {
  return fixLessonAfterServer(raw.body)
}

function fixLessonAfterServer<TLesson extends Lesson = Lesson>(
  raw: any
): ValidatedResource<TLesson> {
  const result = raw
  // result.subjects = unpackStrings(result.subjects);
  // result.grades = unpackStrings(result.grades);
  return result as ValidatedResource<TLesson>
}

export function serverDuplicateLesson(
  lessonId: string
): Promise<ValidatedResource<Lesson>> {
  return serverPost(`/api/lessons/duplicate/${lessonId}`, {}).then(
    fixLessonFromServerResponse
  )
}

export function serverUpdateLessonScreensOrder(
  id: string,
  orderedIds: string[]
): Promise<any> {
  return serverPost(`/api/lessons/${id}/reorder`, orderedIds, {
    parseResponse: false,
  })
}

export function serverCreateLesson(
  lesson: Lesson
): Promise<ValidatedResource<Lesson>> {
  return serverPost("/api/lessons/", prepareLessonForServer(lesson)).then(
    fixLessonFromServerResponse
  )
}

export function serverSaveLesson(
  lesson: Lesson
): Promise<ValidatedResource<Lesson>> {
  return serverPut(
    `/api/lessons/${lesson.id}`,
    prepareLessonForServer(lesson)
  ).then(fixLessonFromServerResponse)
}

export function serverChangeLessonVisibility(
  lessonId: string,
  isPublic: boolean
): Promise<boolean> {
  return serverPut(
    `/api/lessons/${lessonId}/change-visibility/${isPublic}`,
    null
  ).then((raw) => raw.body as boolean)
}

export function serverGetLessonForEditing(
  id: string
): Promise<LessonWithValidation | null> {
  return serverGet(`/api/lessons/for-edit/${id}`, { is404Expected: true }).then(
    (resp) => {
      if (resp.statusCode !== 404) {
        return resp.body as LessonWithValidation
      } else {
        return null
      }
    }
  )
}

export function serverGetLesson(
  id: string
): Promise<LessonWithValidation | null> {
  return serverGet(`/api/lessons/${id}`, { is404Expected: true }).then(
    (resp) => {
      if (resp.statusCode !== 404) {
        return resp.body as LessonWithValidation
      } else {
        return null
      }
    }
  )
}

export function serverUpdateLesson(lesson: Lesson): Promise<void> {
  return serverPut(`/api/lessons/${lesson.id}`, lesson).then(() => {})
}

export function serverGetLessons(
  filter: LessonsFilter
): Promise<Page<LessonSearchInfo>> {
  const url = withQuery("/api/lessons", filter)

  return serverGet(url).then((raw) => {
    const page = raw.body as Page<LessonSearchInfo>
    for (let l of page.items) {
      fixLessonAfterServer(l)
    }
    return page
  })
}

export function serverDeleteLesson(id: string): Promise<any> {
  return serverDelete(`/api/lessons/${id}`)
}

export function serverGetStandardGrades(): Promise<string[]> {
  const url = "/api/lessons/standards-grades"
  return serverGet(url).then((raw) => raw.body as string[])
}

export function serverGetStandardSubjects(grade: string): Promise<string[]> {
  const url = `/api/lessons/standards-subjects/${grade}`
  return serverGet(url).then((raw) => raw.body as string[])
}

// [HttpGet("standards/{grade}/{subject}")]
export function serverGetStandards(
  grade: string,
  subject: string
): Promise<Standard[]> {
  const url = `/api/lessons/standards/${grade}/${subject}`
  return serverGet(url).then((raw) => raw.body as Standard[])
}

export function serverLessonAddStandard(
  lessonId: string,
  standardIds: string[]
): Promise<boolean> {
  return serverPut(`/api/lessons/${lessonId}/set-standard`, standardIds).then(
    (raw) => raw.body as boolean
  )
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Classes
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export function serverGetAllSchoolClasses(): Promise<SchoolAllClasses> {
  return serverGet("/api/classes/all-school-classes").then(
    (raw) => raw.body as SchoolAllClasses
  )
}

export function serverSearchClasses(
  q: string
): Promise<ClassSearchResponse[]> {
  return serverGet(withQuery("/api/classes/search", { q })).then(
    (raw) => raw.body as ClassSearchResponse[]
  )
}

export function serverGetClass(id: string): Promise<ClassResponse> {
  return serverGet(`/api/classes/${id}`).then(
    (raw) => raw.body as ClassResponse
  )
}

export function serverGetClassReports(
  classId: string,
  page: number
): Promise<Page<ReportIndexForWeb>> {
  classId = encodeURIComponent(classId)

  return serverGet(`/api/classes/${classId}/reports?page=${page}`).then(
    (raw) => raw.body as Page<ReportIndexForWeb>
  )
}
export function serverCreateClass(
  schoolId: string,
  name: string,
  grade: string
): Promise<ClassResponse> {
  return serverPost(`/api/classes`, {
    schoolId,
    name,
    grade,
  }).then((raw) => raw.body as ClassResponse)
}

export function serverDeleteClass(id: string): Promise<any> {
  return serverDelete(`/api/classes/${id}`)
}

export function serverRemoveStudentFromClass(data: {
  studentId: string
  classId: string
}): Promise<any> {
  const studentId = encodeURIComponent(data.studentId)
  const classId = encodeURIComponent(data.classId)
  return serverDelete(`/api/students/${studentId}/class/${classId}`)
}

export function serverUpdateClassName(
  id: string,
  newName: string
): Promise<void> {
  return serverPut(`/api/classes/${id}/name`, { name: newName }).then(() => {})
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Homework - Sessions
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export function serverGetPageOfPlaySession(
  page: number
): Promise<Page<PlaySessionWithStats>> {
  return serverGet(`/api/play-session/page/${page}`).then((raw) => {
    var result = raw.body as Page<PlaySessionWithStats>
    return result
  })
}

export function serverCreatePlaySessionForLesson(
  session: PlaySession
): Promise<PlaySession> {
  return serverPost(`/api/play-session/`, session).then(
    (raw) => raw.body as PlaySession
  )
}

export function serverCreatePlaySessionForSingleSeat(
  session: PlaySession
): Promise<PlaySession> {
  return serverPost(`/api/play-session/single-seat-mode`, session).then(
    (raw) => raw.body as PlaySession
  )
}

export function serverGetPlaySession(sessionId: string): Promise<PlaySession> {
  return serverGet(`/api/play-session/${sessionId}`).then(
    (raw) => raw.body as PlaySession
  )
}

//TODO rename this function. the build systems didn't regenerate the with the original name
export function serverUpdatePlaySession3(
  id: string,
  endDate: Date
): Promise<Date> {
  return serverPut(`/api/play-session/${id}`, endDate).then(
    (raw) => raw.body as Date
  )
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Admin initial state
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export function serverGetAdminState(): Promise<AdminInitialState> {
  return serverGet("/api/admin/initial").then(
    (raw) => raw.body as AdminInitialState
  )
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Reports
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

type ReportsCache = {
  [name: string]: ReportWithPlaySessionAndStats
}

const cachedReports: ReportsCache = {}

export function serverGetReport(
  id: string
): Promise<ReportWithPlaySessionAndStats> {
  // if (cachedReports.hasOwnProperty(id)) {
  //   const report = cachedReports[id]
  //   return new Promise<ReportWithPlaySessionAndStats>((resolve) =>
  //     resolve(report)
  //   )
  // } else {
  return serverGet(`/api/reports/${id}`).then((raw) => {
    const result = raw.body as ReportWithPlaySessionAndStats
    cachedReports[id] = result
    return result
  })
  // }
}

export function serverGetReportsForCurrentStudent(opts: {
  page: number
}): Promise<Page<Report>> {
  return serverGet(`/api/reports/current-student/page/${opts.page}`).then(
    (raw) => raw.body as Page<Report>
  )
}

export function serverGetReportsForStudent(opts: {
  classId: string
  studentId: string
  page: number
}): Promise<UserReportsPage> {
  // class/{classId}/student/{studentId}/page/{page}
  const studentId = encodeURIComponent(opts.studentId.trim())
  const classId = encodeURIComponent(opts.classId.trim())
  return serverGet(
    `/api/reports/class/${classId}/student/${studentId}/page/${opts.page}`
  ).then((raw) => raw.body as UserReportsPage)
}

export function serverGetReportForStudent(opts: {
  classId: string
  studentId: string
  reportId: string
}): Promise<StudentReport> {
  // class/{classId}/student/{studentId}/report/{reportId}
  const studentId = encodeURIComponent(opts.studentId.trim())
  const classId = encodeURIComponent(opts.classId.trim())
  const reportId = encodeURIComponent(opts.reportId.trim())
  return serverGet(
    `/api/reports/class/${classId}/student/${studentId}/report/${reportId}`
  ).then((raw) => raw.body as StudentReport)
}

export function serverGetReportForCurrentStudent(opts: {
  reportId: string
}): Promise<Report | null> {
  return serverGet(`/api/reports/current-student/${opts.reportId}`, {
    is404Expected: true,
  }).then((raw) => {
    if (raw.statusCode == 200) {
      return raw.body as Report
    } else {
      return null
    }
  })
}

export function serverGetPageOfReports(
  page: number
): Promise<Page<ReportIndexForWeb>> {
  return serverGet(`/api/reports/page/${page}`).then(
    (raw) => raw.body as Page<ReportIndexForWeb>
  )
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Organization
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export enum OrgTeachersError {
  None,
  NoSchoolId,
  NoOrders,
}

export type OrgLicense = {
  id: string
  name?: string
  email: string
  userId?: string
  licenseDate?: string
}

export type OrgTeacher = {
  id: string
  name?: string
  email: string
  isOrgMaster: boolean
  licenseDate?: string
}
export type OrgTeachersResponse = {
  error: OrgTeachersError
  countPurchasedLicenses: number
  teachers: OrgTeacher[]
  licenses: OrgLicense[]
  canAddTeachers: boolean
}

export function serverGetOrgTeachers(): Promise<OrgTeachersResponse> {
  return serverGet("/api/org/teachers").then(
    (x) => x.body as OrgTeachersResponse
  )
}

export enum AddOrgTeacherError {
  None,
  UserNotInSchool,
  NoOrdersForThisSchool,
  OrderHasNoEndDate,
  LicenceLimitReached,
  TeacherLimitReached,
  FailedToCreateTeacher,
  InvalidEmail,
  FailedToSendInvitationUrl,
  SchoolNotFound,
}

export type AddOrgTeacherResponse = {
  error: AddOrgTeacherError
  licenseLimit: number
  countLicenses: number
  countTeachers: number
  createdTeacher?: OrgTeacher
  createdLicense?: OrgLicense
}

export type AddOrgTeacherRequest = {
  name: string
  email: string
}

export function serverAddOrgTeacher(
  request: AddOrgTeacherRequest
): Promise<AddOrgTeacherResponse> {
  return serverPost("/api/org/teachers", request).then(
    (x) => x.body as AddOrgTeacherResponse
  )
}

export type UpdateSchoolNameRequest = {
  name: string
}

export type UpdateSchoolLogoRequest = {
  logoUrl: string
}

export function serverUpdateOrgName(
  request: UpdateSchoolNameRequest
): Promise<RawRequestResult> {
  return serverPost("/api/org/name", request)
}

export function serverUpdateOrgLogo(
  request: UpdateSchoolLogoRequest
): Promise<RawRequestResult> {
  return serverPost("/api/org/logo", request)
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Students
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export function serverUpdateStudent(data: Student): Promise<void> {
  return serverPut(`/api/students/`, data).then((body) => {})
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Play hub
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export function serverPlayToggleTimer(opts: {
  connectionId: string
  enable: boolean
  secondsRemaining: number
}): Promise<ToggleTimerResponse> {
  const connectionId = encodeURIComponent(opts.connectionId)
  const body: ToggleTimerRequest = {
    enable: opts.enable,
    secondsRemaining: opts.secondsRemaining,
    time: new Date(),
  }

  return serverPost(`/api/playhub/toggle-timer/${connectionId}`, body).then(
    (raw) => raw.body as ToggleTimerResponse
  )
}

export function serverPlayGetTime(): Promise<Date> {
  return serverGet(`/api/playhub/server-time`).then((raw) => raw.body as Date)
}

export function serverPlayGetUser(): Promise<LoggedInUser> {
  return serverGet(`/api/playhub/me`, { redirectOn401: false }).then(
    (resp) => resp.body as LoggedInUser
  )
}

export function serverPlayJoin(opts: {
  gameCode: string
  fullName?: string | null
}): Promise<JoinResponse> {
  console.log("serverPlayJoin", opts)
  const gameCode = encodeURIComponent(opts.gameCode)
  const fullName = hasText(opts.fullName)
    ? encodeURIComponent(opts.fullName!)
    : ""
  return serverGet(`/api/playhub/join/${gameCode}/${fullName}`).then(
    (raw) => raw.body as JoinResponse
  )
}

export function serverPlayExit(connectionId: string): Promise<void> {
  const cid = encodeURIComponent(connectionId)
  return serverDelete(`/api/playhub/student/${cid}`).then((raw) => {})
}

export function serverPlayReviewPrevious(
  connectionId: string
): Promise<ReviewScreenResponse> {
  connectionId = encodeURIComponent(connectionId)

  return serverPost(`/api/playhub/review-previous/${connectionId}`, {}).then(
    (x) => x.body as ReviewScreenResponse
  )
}

export function serverPlayReviewNext(
  connectionId: string
): Promise<ReviewScreenResponse> {
  connectionId = encodeURIComponent(connectionId)

  return serverPost(`/api/playhub/review-next/${connectionId}`, {}).then(
    (x) => x.body as ReviewScreenResponse
  )
}

// An answer the network dropped is sent again: the sessions refuse a second
// answer to a screen (AlreadyAnswered), so a repeat cannot count twice or be
// taken for the next screen's answer.
const ANSWER_OPTIONS = { retryWhenNoResponse: true }

export function serverPlayPollAnswer(
  connectionId: string,
  request: PollAnswerRequest
): Promise<ScreenFinishedResponse> {
  connectionId = encodeURIComponent(connectionId)

  return serverPost(
    `/api/playhub/poll-answer/${connectionId}`,
    request,
    ANSWER_OPTIONS
  ).then(
    (raw) => raw.body as ScreenFinishedResponse
  )
}

export function serverPlayQuizAnswer(
  connectionId: string,
  request: QuizAnswerRequest
): Promise<ScreenFinishedResponse> {
  connectionId = encodeURIComponent(connectionId)

  return serverPost(
    `/api/playhub/quiz-answer/${connectionId}`,
    request,
    ANSWER_OPTIONS
  ).then(
    (raw) => raw.body as ScreenFinishedResponse
  )
}

export function serverPlayMapAnswer(
  connectionId: string,
  request: MapAnswerRequest
): Promise<ScreenFinishedResponse> {
  connectionId = encodeURIComponent(connectionId)

  return serverPost(
    `/api/playhub/map-answer/${connectionId}`,
    request,
    ANSWER_OPTIONS
  ).then(
    (raw) => raw.body as ScreenFinishedResponse
  )
}

export function serverPlayDrawingSubmit(
  connectionId: string,
  request: DrawingSubmitRequest
): Promise<ScreenFinishedResponse> {
  connectionId = encodeURIComponent(connectionId)

  return serverPost(
    `/api/playhub/drawing-submit/${connectionId}`,
    request,
    ANSWER_OPTIONS
  ).then((raw) => raw.body as ScreenFinishedResponse)
}

export function serverPlayDragDropAnswer(
  connectionId: string,
  request: DragDropAnswerRequest
): Promise<ScreenFinishedResponse> {
  connectionId = encodeURIComponent(connectionId)

  return serverPost(
    `/api/playhub/dragdrop-answer/${connectionId}`,
    request,
    ANSWER_OPTIONS
  ).then((raw) => raw.body as ScreenFinishedResponse)
}

export function serverPlayOpenTextAnswer(
  connectionId: string,
  request: OpenTextAnswerRequest
): Promise<ScreenFinishedResponse> {
  connectionId = encodeURIComponent(connectionId)

  return serverPost(
    `/api/playhub/open-text-answer/${connectionId}`,
    request,
    ANSWER_OPTIONS
  ).then((raw) => raw.body as ScreenFinishedResponse)
}

export function serverPlayKeyboardAnswer(
  connectionId: string,
  request: KeyboardAnswerRequest
): Promise<ScreenFinishedResponse> {
  connectionId = encodeURIComponent(connectionId)

  return serverPost(
    `/api/playhub/keyboard-answer/${connectionId}`,
    request,
    ANSWER_OPTIONS
  ).then((raw) => raw.body as ScreenFinishedResponse)
}

export function serverPlayConnectAnswer(
  connectionId: string,
  request: ConnectAnswerRequest
): Promise<ScreenFinishedResponse> {
  connectionId = encodeURIComponent(connectionId)

  return serverPost(
    `/api/playhub/connect-answer/${connectionId}`,
    request,
    ANSWER_OPTIONS
  ).then((raw) => raw.body as ScreenFinishedResponse)
}

export function serverPlayNextScreen(
  connectionId: string,
  request: NextScreenRequest
): Promise<ScreenFinishedResponse | undefined> {
  connectionId = encodeURIComponent(connectionId)

  return serverPost(`/api/playhub/next-screen/${connectionId}`, request).then(
    (raw) => raw.body as ScreenFinishedResponse
  )
}

export function serverPlayReconnectSession(
  connectionId: string
): Promise<ReconnectSessionResponse> {
  if (!hasText(connectionId)) {
    return Promise.reject("Empty connection id")
  }

  connectionId = encodeURIComponent(connectionId)

  return serverGet(`/api/playhub/reconnect-session/${connectionId}`, {
    is404Expected: true,
  }).then((raw) => {
    if (raw.statusCode != 404) {
      return { result: raw.body as ReconnectResponse, success: true }
    } else {
      return { result: null, success: false }
    }
  })
}

export function serverPlayTimerTimedOut(connectionId: string): Promise<void> {
  connectionId = encodeURIComponent(connectionId)

  return serverGet(`/api/playhub/timer-timeout/${connectionId}`).then(
    (raw) => {}
  )
}

interface ReconnectTeacherRequest {
  oldConnectionId: string
  lessonId: string
  enableMemes: boolean
  classId: string | null | undefined
  enableGraphicalLogin: boolean
}

export function serverPlayReconnectTeacher(
  request: ReconnectTeacherRequest
): Promise<ReconnectTeacherResponse> {
  return serverPost(`/api/playhub/reconnect-teacher/`, request).then(
    (raw) => raw.body as ReconnectTeacherResponse
  )
}

export function serverPlayRemoveStudent(request: {
  teacherConnectionId: string
  studentConnectionId: string
}): Promise<ClientStudent | null> {
  const teacherConnectionId = encodeURIComponent(request.teacherConnectionId)
  const studentConnectionId = encodeURIComponent(request.studentConnectionId)

  return serverDelete(
    `/api/playhub/remove-student/${teacherConnectionId}/${studentConnectionId}`,
    { is404Expected: true }
  ).then((raw) => {
    if (raw.statusCode != 404) {
      return raw.body as ClientStudent
    } else {
      return null
    }
  })
}

export function serverPlayStart(
  connectionId: string
): Promise<ClassroomGameStartedTeacherResponse> {
  connectionId = encodeURIComponent(connectionId)

  return serverPost(`/api/playhub/start/${connectionId}`, {}).then(
    (raw) => raw.body as ClassroomGameStartedTeacherResponse
  )
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Payments
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

type CreatePaymentSessionRequest = {
  purchaseType: KnownOrderPackageTypes
}

type CreatePaymentSessionResponse = {
  redirectTo: string
}

export function serverCreatePaymentSession(
  req: CreatePaymentSessionRequest
): Promise<CreatePaymentSessionResponse> {
  return serverPost(`/api/payments/create-session`, req).then(
    (raw) => raw.body as CreatePaymentSessionResponse
  )
}

type StripeProductLineInfo = {
  amount: number
  productName: string
}

export type GetStripeSessionInfoResponse = {
  license?: License | null
  items: StripeProductLineInfo[]
}

export function serverGetPaymentInfo(
  sessionId: string
): Promise<GetStripeSessionInfoResponse> {
  const encoded = encodeURIComponent(sessionId)
  return serverGet(`/api/payments/session/${encoded}`).then(
    (raw) => raw.body as GetStripeSessionInfoResponse
  )
}

export function serverGetPaymentInvoiceNumber(
  type: PaymentType
): Promise<string> {
  return serverGet(withQuery("/api/payments/invoice-number", { type })).then(
    (raw) => raw.body as string
  )
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Real-Time Events
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export function serverGetPendingMessages(
  connectionId: string
): Promise<PendingMessagesResponse> {
  return serverGet(`/api/playhub/pending-messages/${connectionId}`).then(
    (raw) => {
      const resp = raw.body as PendingMessagesResponse
      resp.messages.forEach((msg) => {
        if (msg.timestamp) {
          msg.timestamp = new Date(msg.timestamp)
        }
      })
      return resp
    }
  )
}

export function serverNewClassroomGame(
  game: NewClassroomGameOptions
): Promise<NewClassroomGameResponse> {
  console.log("serverNewClassroomGame", game)
  return serverPost(`/api/playhub/classroom-game/${game.lessonId}`, {
    certificateMinSuccessRate: game.certificateMinSuccessRate,
    classId: game.classId,
    disableMemes: game.disableMemes,
    enableTimer: game.enableTimer,
    enableGraphicalLogin: game.enableGraphicalLogin,
    shuffleAnswers: game.shuffleAnswers,
    shuffleScreens: game.shuffleScreens,
  } as NewClassroomGameRequest).then(
    (raw) => raw.body as NewClassroomGameResponse
  )
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Videos
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export function serverSearchKhanVideos(args: {
  lastId: number
  q: string
  lang?: string | null
}): Promise<SimplePage<KhanAcademyVideo>> {
  return serverGet(withQuery(`/api/video/khan-academy`, args)).then(
    (x) => x.body as SimplePage<KhanAcademyVideo>
  )
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Certificates
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export function serverGetCertificates(): Promise<PlaySessionCertificate[]> {
  return serverGet(`/api/users/certificates`, {
    redirectOn401: false,
  }).then((resp) => {
    const raw = resp.body as PlaySessionCertificateResponse[]
    return raw.map((x) => ({
      ...x,
      date: new Date(x.date),
    }))
  })
}

export function serverUploadImageUrl(params: { type: ImageType; url: string }) {
  return serverPost("/api/upload/image-url", params).then(
    (resp) => resp.body as ImageUploadResult
  )
}

export function serverGetTextFromImage(file: Blob): Promise<string> {
  return serverPost("/api/images/ocr", file).then((raw) => raw.body as string)
}

export function serverImportPdf(file: Blob): Promise<string[]> {
  return serverPost("/api/upload/pdf", file).then((raw) => raw.body as string[])
}

export function serverUploadImage(params: {
  type: ImageType
  onProgress?: (percent: number) => void
  file: Blob
}): Promise<ImageUploadResult> {
  const { onProgress, file, type } = params

  return serverPost(`/api/upload/image?type=${type}`, file, {
    onProgress,
  }).then((raw) => {
    const res = raw.body as ImageUploadResult
    return res
  })
}

export function serverUploadAudio(params: {
  onProgress?: (percent: number) => void
  file: File
}): Promise<AudioUploadResult> {
  const { onProgress, file } = params

  return serverPost(`/api/upload/audio`, file, { onProgress }).then(
    (raw) => raw.body as AudioUploadResult
  )
}

interface AiQuizResult {
  screens: QuizScreen[]
  isRateLimitReached: boolean
  countQueriesRemaining: number
  maxCountQueries: number
  historyCacheKey: string
  page: number
}

export function serverCreateQuizWithChatGpt(
  query: string,
  studyText: string | undefined,
  audience: AiAudience | null | undefined,
  languageCode: string,
  page: number,
  history: string | null
): Promise<AiQuizResult> {
  return serverPost(
    "/api/screens/chat-gpt-quiz",
    { query, studyText, audience, page, history, languageCode },
    { is429TooManyRequestsExpected: true }
  ).then((raw) => raw.body as AiQuizResult)
}

export function serverGetDashboardContents(): Promise<DashboardContents> {
  return serverGet("/api/dashboard").then((x) => x.body as DashboardContents)
}

export function serverGetLatestBlogPosts(): Promise<DashboardBlogPost[]> {
  return serverGet("/api/dashboard/blog").then(
    (x) => x.body as DashboardBlogPost[]
  )
}

export function serverGetDashboardLeaderboard(args: {
  byRegion: boolean
}): Promise<DashboardLeaderboardSchool[]> {
  return serverGet(
    `/api/dashboard/leaderboard?byRegion=${args.byRegion ? "True" : "False"}`
  ).then((x) => x.body as DashboardLeaderboardSchool[])
}

export function serverGetPublicThemes(): Promise<Theme[]> {
  return serverGet("/api/themes").then((x) => x.body as Theme[])
}

export function serverSetLessonTheme(args: {
  themeId: number | null
  lessonId: string
}) {
  const lessonId = encodeURIComponent(args.lessonId)
  return serverPost(`/api/themes/lesson/${lessonId}`, {
    themeId: args.themeId,
  })
}

export function serverGetThemeByPlayCode(gameCode: string) {
  const gameCodeEncoded = encodeURIComponent(gameCode)
  return serverGet(`/api/playhub/theme/${gameCodeEncoded}`).then((x) => {
    if (!!x.body) {
      return x.body as Theme
    } else {
      return null
    }
  })
}
